import { useState } from "react";
import type { InvoiceDto } from "@grizzly/shared";
import { invoiceStatus } from "@grizzly/shared";
import { formatAmount } from "../../lib/format.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { useToast } from "../../lib/useToast.js";
import { api } from "../../lib/apiClient.js";
import type { ApiClientError } from "../../lib/apiClient.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";

export function InvoicePanel({
  invoice,
  onChanged,
}: {
  invoice: InvoiceDto;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      await api.issueInvoice(invoice.id);
      push(t("finance.invoiceIssuedToast"));
      onChanged();
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      await api.recordPayment({
        customerId: invoice.customerId,
        invoiceId: invoice.id,
        amount: (invoice.balanceMinor / 100).toFixed(2),
        method: "eft",
      });
      push(t("finance.paymentRecordedToast"));
      onChanged();
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">
            {invoice.number} · {invoice.customerName}
          </h2>
          <p className="text-xs text-ink-muted">
            {invoice.dueDate ? `${t("finance.dueOn", { date: invoice.dueDate })} · ` : ""}
            {t("finance.totalAmount", { amount: formatAmount(invoice.totalMinor) })}
          </p>
        </div>
        <Badge tone={invoiceStatus(invoice.overdue ? "overdue" : invoice.status).tone}>
          {invoice.overdue
            ? t("status.invoice.overdue")
            : t(`status.invoice.${invoice.status}` as MessageKey)}
        </Badge>
      </div>

      {invoice.lines.length > 0 ? (
        <ul className="mb-3 space-y-1">
          {invoice.lines.map((line) => (
            <li key={line.id} className="flex items-center justify-between text-sm">
              <span>
                {line.description} × {line.quantity}
              </span>
              <span className="tabular text-ink-muted">
                {formatAmount(line.lineTotalMinor)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mb-3 flex justify-between border-t border-line pt-2 text-sm">
        <span>{t("common.fields.paid")}</span>
        <span className="tabular">{formatAmount(invoice.paidMinor)}</span>
      </div>
      <div className="mb-3 flex justify-between text-sm font-semibold">
        <span>{t("finance.outstanding")}</span>
        <span className="tabular">{formatAmount(invoice.balanceMinor)}</span>
      </div>

      <div className="flex gap-2">
        {invoice.status === "draft" ? (
          <Button size="sm" variant="primary" disabled={busy} onClick={issue}>
            {t("finance.issue")}
          </Button>
        ) : null}
        {invoice.status === "issued" || invoice.status === "partially_paid" ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={pay}>
            {t("finance.recordFullPayment")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
